import React from "react";
import { useAnnoScolastico, formatAnnoScolastico } from "@/lib/annoScolastico";
import { useAuth } from "@/lib/AuthContext";

/**
 * Selector "Anno Scolastico attivo" globale.
 * - Persistito in localStorage (`hub_anno_attivo`) + backend `app_settings.anno_corrente`.
 * - Sincronizza bidirezionalmente `useAnnoScolastico()` e `annoCorrente` di AuthContext.
 * - Sizes: "small" (default, header gestione), "large" (Home portale, in evidenza).
 */
export default function AnnoAttivoSelector({ size = "small" }) {
  const { anno, setAnno, anniAvailable } = useAnnoScolastico();
  const { annoCorrente, setAnnoCorrente } = useAuth();

  React.useEffect(() => {
    if (anno != null && annoCorrente != null && anno !== annoCorrente) {
      setAnnoCorrente(anno);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anno]);

  const onChange = async (v) => {
    const n = parseInt(v, 10);
    setAnno(n);
    setAnnoCorrente(n);
    try {
      const { saveAppSettings } = await import("@/lib/api");
      await saveAppSettings({ anno_corrente: n });
    } catch { /* ignore */ }
  };

  if (size === "large") {
    return (
      <div data-testid="anno-attivo-selector-large"
        title="Anno Scolastico attivo — filtra tutte le gestioni compatibili"
        style={{
          display: "inline-flex", alignItems: "center", gap: 10,
          padding: "10px 18px",
          background: "linear-gradient(90deg,#ffdd00,#ffc400)",
          borderRadius: 8, boxShadow: "0 4px 12px rgba(255,221,0,0.35)",
          border: "2px solid rgba(255,255,255,0.5)",
        }}>
        <span style={{ fontSize: 14, fontWeight: 900, color: "#0A2B52", letterSpacing: 1 }}>
          A.S.
        </span>
        <select value={anno ?? ""} onChange={(e) => onChange(e.target.value)}
          data-testid="anno-attivo-select"
          style={{
            background: "white", color: "#0A2B52", fontWeight: 900, fontSize: 20,
            border: "2px solid #0A2B52", padding: "4px 12px", cursor: "pointer",
            borderRadius: 4, letterSpacing: 0.5,
          }}>
          {(anniAvailable.length > 0 ? anniAvailable : [anno]).map(a => (
            <option key={a} value={a}>{formatAnnoScolastico(a)}</option>
          ))}
        </select>
      </div>
    );
  }

  // Small (default) — usato nella barra nav delle gestioni e nelle pagine con filtri
  return (
    <span data-testid="anno-attivo-selector"
      title="Anno Scolastico attivo — filtra tutte le tabelle delle gestioni compatibili"
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "5px 12px",
        background: "linear-gradient(90deg,#0d47a1,#1565c0)",
        borderRadius: 4,
        boxShadow: "0 1px 3px rgba(13,71,161,0.35)",
      }}>
      <span style={{ color: "white", fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" }}>A.S.</span>
      <select value={anno ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid="anno-attivo-select"
        style={{
          padding: "4px 10px", fontSize: 13, fontWeight: 700,
          background: "white", color: "#0d47a1",
          border: "2px solid white", borderRadius: 3,
          letterSpacing: 0.3, cursor: "pointer",
        }}>
        {(anniAvailable.length > 0 ? anniAvailable : [anno]).map(a => (
          <option key={a} value={a}>{formatAnnoScolastico(a)}</option>
        ))}
      </select>
    </span>
  );
}
